import { copyToClipboard } from "./commonHelper";

const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let index = 0; index < binaryString.length; index += 1) {
        bytes[index] = binaryString.charCodeAt(index);
    }
    return bytes;
};

const decodeBdata = (bdata, dtype) => {
    try {
        const bytes = base64ToUint8Array(bdata);
        const buffer = bytes.buffer;

        switch (dtype) {
            case "i1":
            case "int8":
                return Array.from(new Int8Array(buffer));
            case "u1":
            case "uint8":
                return Array.from(new Uint8Array(buffer));
            case "i2":
            case "int16":
                return Array.from(new Int16Array(buffer));
            case "u2":
            case "uint16":
                return Array.from(new Uint16Array(buffer));
            case "i4":
            case "int32":
                return Array.from(new Int32Array(buffer));
            case "u4":
            case "uint32":
                return Array.from(new Uint32Array(buffer));
            case "f4":
            case "float32":
                return Array.from(new Float32Array(buffer));
            case "f8":
            case "float64":
                return Array.from(new Float64Array(buffer));
            case "i8":
            case "int64":
                if (typeof window !== "undefined" && window.BigInt64Array) {
                    return Array.from(new window.BigInt64Array(buffer)).map(Number);
                }
                console.warn("BigInt64Array is not supported in this environment.");
                return [];
            case "u8":
            case "uint64":
                if (typeof window !== "undefined" && window.BigUint64Array) {
                    return Array.from(new window.BigUint64Array(buffer)).map(Number);
                }
                console.warn("BigUint64Array is not supported in this environment.");
                return [];
            default:
                console.warn("Unknown dtype:", dtype);
                return Array.from(bytes);
        }
    } catch (error) {
        console.error("Failed to decode bdata:", error);
        return [];
    }
};

export const decodeObjectBdata = (value) => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) return value.map(decodeObjectBdata);
    if (value.bdata !== undefined && value.dtype !== undefined) {
        return decodeBdata(value.bdata, value.dtype);
    }

    return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, decodeObjectBdata(item)])
    );
};

const MAX_SNAPSHOT_POINTS = 16;
const TRACE_ARRAY_KEYS = ["x", "y", "text", "hovertext", "customdata", "ids"];

const limitTrace = (trace) => {
    if (!trace || trace.type === "pie") return trace;

    const pointCount = Math.max(
        Array.isArray(trace.x) ? trace.x.length : 0,
        Array.isArray(trace.y) ? trace.y.length : 0
    );
    if (pointCount <= MAX_SNAPSHOT_POINTS) return trace;

    const limitedTrace = { ...trace };
    TRACE_ARRAY_KEYS.forEach((key) => {
        if (Array.isArray(trace[key]) && trace[key].length === pointCount) {
            limitedTrace[key] = trace[key].slice(0, MAX_SNAPSHOT_POINTS);
        }
    });
    return limitedTrace;
};

const formatGraph = (graph, isMobile) => {
    const data = graph.data.map((trace) => limitTrace(decodeObjectBdata(trace)));
    const pointCount = Math.max(
        ...data.map((trace) => (
            trace?.x?.length || trace?.y?.length || trace?.labels?.length || 0
        )),
        0
    );
    const hasHorizontalBars = data.some(
        (trace) => trace?.type === "bar" && trace?.orientation === "h"
    );
    const legendSpace = isMobile && data.length > 1
        ? Math.min(220, 40 + data.length * 18)
        : 0;

    return {
        ...graph,
        data,
        layout: {
            ...(graph.layout || {}),
            width: undefined,
            height: isMobile
                ? 420 + legendSpace
                : hasHorizontalBars
                    ? Math.min(620, Math.max(360, pointCount * 30 + 130))
                    : 420,
            margin: {
                l: isMobile ? 48 : 50,
                r: isMobile ? 10 : 25,
                t: isMobile ? 56 : 64,
                b: isMobile
                    ? Math.max(legendSpace, pointCount > 8 ? 100 : 56)
                    : pointCount > 8 ? 110 : 44,
            },
            xaxis: {
                ...(graph.layout?.xaxis || {}),
                ...(pointCount > 8 && !hasHorizontalBars
                    ? {
                        tickangle: isMobile ? -45 : -35,
                        tickfont: { size: isMobile ? 8 : 10 },
                    }
                    : {}),
            },
            legend: isMobile && data.length > 1
                ? {
                    ...(graph.layout?.legend || {}),
                    orientation: "h",
                    x: 0,
                    xanchor: "left",
                    y: -0.22,
                    yanchor: "top",
                    font: { ...(graph.layout?.legend?.font || {}), size: 9 },
                }
                : graph.layout?.legend,
        },
    };
};

export const formatProcurementGraphSnapshot = (graph, isMobile) => {
    if (!graph) return graph;
    return Array.isArray(graph)
        ? graph.map((item) => formatGraph(item, isMobile))
        : formatGraph(graph, isMobile);
};

const getAxisTitle = (axis, fallback) => (
    typeof axis?.title === "string" ? axis.title : axis?.title?.text || fallback
);

const getTraceTableData = (trace, index, layout, decodeTrace) => {
    const decodedTrace = decodeTrace(trace);
    const isPie = decodedTrace?.type === "pie";
    const isHorizontalBar = decodedTrace?.type === "bar" && decodedTrace?.orientation === "h";

    return {
        categories: (isPie
            ? decodedTrace.labels
            : isHorizontalBar ? decodedTrace.y : decodedTrace.x) || [],
        values: (isPie
            ? decodedTrace.values
            : isHorizontalBar ? decodedTrace.x : decodedTrace.y) || [],
        categoryHeader: isPie
            ? "Label"
            : getAxisTitle(isHorizontalBar ? layout?.yaxis : layout?.xaxis, "Category"),
        valueHeader: decodedTrace?.name
            || getAxisTitle(isHorizontalBar ? layout?.xaxis : layout?.yaxis, `Value ${index + 1}`),
    };
};

const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const normalizeColumnName = (value) => String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const restoreTraceDataFromRows = (traces, sourceRows) => {
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) return traces;

    const rowKeys = Object.keys(sourceRows[0]);
    return traces.map((trace) => {
        const categoryKey = rowKeys.find(
            (key) => normalizeColumnName(key) === normalizeColumnName(trace.categoryHeader)
        );
        const valueKey = rowKeys.find(
            (key) => normalizeColumnName(key) === normalizeColumnName(trace.valueHeader)
        );

        if (!categoryKey || !valueKey) return trace;
        return {
            ...trace,
            categories: sourceRows.map((row) => row[categoryKey]),
            values: sourceRows.map((row) => row[valueKey]),
        };
    });
};

export const createGraphTableClipboardData = (
    graph,
    decodeTrace = decodeObjectBdata,
    sourceRows = []
) => {
    const graphTraces = (graph?.data || []).map((trace, index) => (
        getTraceTableData(trace, index, graph?.layout, decodeTrace)
    ));
    const traces = restoreTraceDataFromRows(graphTraces, sourceRows);
    if (traces.length === 0) return null;

    const shareCategories = traces.every((trace) => (
        JSON.stringify(trace.categories) === JSON.stringify(traces[0].categories)
    ));
    let headers;
    let rows;

    if (shareCategories) {
        const rowCount = Math.max(
            traces[0].categories.length,
            ...traces.map((trace) => trace.values.length)
        );
        headers = [traces[0].categoryHeader, ...traces.map((trace) => trace.valueHeader)];
        rows = Array.from({ length: rowCount }, (_, rowIndex) => [
            traces[0].categories[rowIndex] ?? rowIndex + 1,
            ...traces.map((trace) => trace.values[rowIndex] ?? ""),
        ]);
    } else {
        const rowCount = Math.max(
            ...traces.map((trace) => Math.max(trace.categories.length, trace.values.length))
        );
        headers = traces.flatMap((trace) => [
            `${trace.valueHeader} ${trace.categoryHeader}`,
            trace.valueHeader,
        ]);
        rows = Array.from({ length: rowCount }, (_, rowIndex) => (
            traces.flatMap((trace) => [
                trace.categories[rowIndex] ?? rowIndex + 1,
                trace.values[rowIndex] ?? "",
            ])
        ));
    }

    return {
        text: [
            headers.join("\t"),
            ...rows.map((row) => row.map((cell) => String(cell ?? "")).join("\t")),
        ].join("\n"),
        html: `<table><thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")}</tbody></table>`,
    };
};

export const copyGraphDataToClipboard = async (graph, sourceRows = []) => {
    const clipboardData = createGraphTableClipboardData(
        graph,
        decodeObjectBdata,
        sourceRows
    );
    return clipboardData ? copyToClipboard(clipboardData) : false;
};
